import React, { useEffect, useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { connect } from 'dva';
import { Form, Input, Spin, Row, Col, Card, Checkbox, DatePicker, InputNumber, Select } from 'antd';
import moment from 'moment';
import ButtonBar from '../../ButtonBar/ButtonBar';
import { ACTION_APP_ADD_NOTIFICATION, ACTION_APP_GO_TO_ROUTE } from '../../../models/app';
import { ACTION_HEADER_UPDATE } from '../../../models/header';
import { ROUTE_CLIENTS_LIST } from '../../../routes';
import styles from './../styles.less';
import { ACTION_CLIENTS_CREATE, ACTION_CLIENTS_LOAD_ITEM, ACTION_CLIENTS_UPDATE } from '../../../models/clients';
import IspEmails from '../IspEmails';
import { clientStatusToTextLabel } from '../constants';
import { ACTION_CLIENT_PACKAGES_LOAD_FULL_LIST } from '../../../models/clientPackages';
import RecognitionUsages from './RecognitionUsages';

const { Item } = Form;

const analyticsTypes = [
  {
    id: 'matomo',
    name: 'Matomo',
  },
  {
    id: 'google_analytics',
    name: 'Google Analytics',
  },
];

function disabledDate(current) {
  // Can not select days before today and today
  return current && current < moment().endOf('day');
}

const ClientsItem = (props) => {
  const { loading, form, intl } = props;
  const projectId = props.app.currentProjectId || props.match.params.projectId;
  const clientId = props.match.params.clientId || null;
  const [client, setClient] = useState(null);

  useEffect(() => {
    props.dispatch({ type: ACTION_CLIENT_PACKAGES_LOAD_FULL_LIST });

    if (clientId) {
      const payload = {
        id: clientId,
      };
      props
        .dispatch({ type: ACTION_CLIENTS_LOAD_ITEM, payload })
        .then((response) => {
          const setFields = {
            is_active: {
              value: !response.is_disabled,
            },
          };
          if (response.settings.feature_mark_as_isp) {
            setFields.settings = {
              feature_mark_as_isp: {
                value: true,
              },
            };
          }
          if (response.settings.feature_ai_outreach_agents) {
            setFields.settings = {
              feature_ai_outreach_agents: {
                value: true,
              },
            };
          }

          setClient({
            id: clientId,
            name: response.name,
            is_active: !response.is_disabled,
            disabled_at: response.disabled_at ? moment(response.disabled_at) : null,
            signed_till: response.signed_till ? moment(response.signed_till) : null,
            status: response.status,
            email: response.email,
            client_package_id: response.client_package ? response.client_package.id : null,
            settings: response.settings || {},
            contact_enrichment_calls_budget: response.contact_enrichment_calls_budget,
            contact_searches_calls_budget: response.contact_searches_calls_budget,
          });
          setTimeout(() => {
            form.setFields(setFields);
          }, 0);
        });
    }

    props.dispatch({
      type: ACTION_HEADER_UPDATE,
      payload: {
        title: clientId
          ? 'Component.Administration.Clients.Edit.Form.Title'
          : 'Component.Administration.Clients.New.Form.Title',
      },
    });
  },
  []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const { getFieldsValue, validateFieldsAndScroll } = form;
    validateFieldsAndScroll([
      'name',
      'email',
      'settings',
      'contact_enrichment_calls_budget',
      'contact_searches_calls_budget',
      'status',
      'signed_till',
      'client_package_id',
    ], (err) => {
      if (!err) {
        // eslint-disable-next-line prefer-const
        const {
          name,
          email,
          settings,
          is_active,
          disabled_at,
          status,
          signed_till,
          client_package_id,
        } = getFieldsValue();
        let {
          contact_enrichment_calls_budget,
          contact_searches_calls_budget,
        } = getFieldsValue();
        if (!settings.feature_mark_as_isp_emails) {
          settings.feature_mark_as_isp_emails = [];
        }

        if (!settings.feature_ai_outreach_agents) {
          contact_enrichment_calls_budget = 0;
          contact_searches_calls_budget = 0;
        }

        if (settings.analytic_type !== 'matomo') {
          settings.matomo_base_uri = settings.matomo_base_uri || '';
          settings.matomo_api_key = settings.matomo_base_uri || '';
        }

        const data = {
          name,
          email,
          settings,
          contact_enrichment_calls_budget,
          contact_searches_calls_budget,
          status,
          client_package_id: client_package_id || null,
          is_disabled: !is_active,
          disabled_at: is_active && disabled_at ? disabled_at.format('YYYY-MM-DD') : null,
          signed_till: status === 'signed' ? signed_till : null,
        };

        saveClient(data);
      }
    });
  };

  const saveClient = async (data) => {
    const id = client ? client.id : null;
    const success = await props.dispatch({
      type: id ? ACTION_CLIENTS_UPDATE : ACTION_CLIENTS_CREATE,
      payload: {
        id,
        data,
      },
    });
    if (success) {
      if (!!id && (+props.app.selectedClient.id === +id)) {
        props.dispatch({
          type: 'app/getDefaultSettings',
        });
      }
      addNotification(id);
      goToClients();
    }
  };

  const goToClients = () => {
    props.dispatch({
      type: ACTION_APP_GO_TO_ROUTE,
      payload: { routeName: ROUTE_CLIENTS_LIST, projectId },
    });
  };

  const addNotification = (id) => {
    props.dispatch({
      type: ACTION_APP_ADD_NOTIFICATION,
      payload: {
        type: 'success',
        message: 'Common.Success',
        description: id
          ? 'Component.Administration.Clients.Edit.Success.Text'
          : 'Component.Administration.Clients.New.Success.Text',
      },
    });
  };

  const values = form.getFieldsValue();

  return (
    <Spin
      spinning={
        !!loading.effects[ACTION_CLIENTS_CREATE] ||
        !!loading.effects[ACTION_CLIENTS_UPDATE] ||
        !!loading.effects[ACTION_CLIENTS_LOAD_ITEM]
      }
    >
      <div className={styles.containerLarge}>
        <Form onSubmit={handleSubmit}>
          <Card>
            <Row>
              <Col>
                <Item
                  label={
                    <FormattedMessage id="Component.Administration.Clients.Form.Name" />
                  }
                >
                  {form.getFieldDecorator('name', {
                    initialValue: client ? client.name : '',
                    rules: [
                      {
                        required: true,
                        min: 3,
                        max: 255,
                        message: intl.formatMessage({
                          id: 'Component.Administration.Clients.Validation.Name',
                        }),
                      },
                    ],
                  })(<Input maxLength={255} type="text" />)}
                </Item>
              </Col>
              <Col>
                <Item
                  label={
                    <FormattedMessage id="Component.Administration.Clients.Form.Email" />
                  }
                >
                  {form.getFieldDecorator('email', {
                    initialValue: client ? client.email : '',
                    rules: [
                      {
                        required: true,
                        min: 3,
                        max: 255,
                        type: 'email',
                        message: intl.formatMessage({
                          id: 'Component.Administration.Clients.Validation.Email',
                        }),
                      },
                    ],
                  })(<Input maxLength={255} type="text" />)}
                </Item>
              </Col>
            </Row>
            <Row gutter={32}>
              <Col span={12}>
                <Item
                  label={
                    <FormattedMessage id="Component.Administration.Clients.Status" />
                  }
                >
                  {form.getFieldDecorator('status', {
                    rules: [{
                      required: true,
                    }],
                    initialValue: client ? client.status : '',
                  })(<Select>
                    {(Object.entries(clientStatusToTextLabel)).map(([key, label]) => (
                      <Select.Option value={key} key={key}>
                        <FormattedMessage id={label} />
                      </Select.Option>))}
                  </Select>)}
                </Item>
              </Col>
              {values.status === 'signed' && (
                <Col span={12}>
                  <Item
                    label={
                      <FormattedMessage id="Component.Administration.Clients.SignedTill" />
                    }
                  >
                    {form.getFieldDecorator('signed_till', {
                      initialValue: client ? client.signed_till : '',
                      rules: [
                        {
                          required: true,
                        },
                      ],
                    })(<DatePicker type="date" />)}
                  </Item>
                </Col>
              )}
            </Row>
            <Row gutter={32}>
              <Col span={12}>
                <Item
                  label={
                    <FormattedMessage id="Component.Administration.Clients.Package" />
                  }
                >
                  {form.getFieldDecorator('client_package_id', {
                    initialValue: client && client.client_package_id ? client.client_package_id : undefined,
                  })(
                    <Select
                      allowClear
                      placeholder={intl.formatMessage({
                        id: 'Component.Administration.Clients.Package.Placeholder',
                      })}
                    >
                      {props.clientPackages.clientPackagesFullList.map(item => (
                        <Select.Option value={item.id} key={item.id}>
                          {item.title}
                        </Select.Option>
                      ))}
                    </Select>,
                  )}
                </Item>
              </Col>
            </Row>
            <Row gutter={16} className={styles.marginTop16}>
              <Col span={4}>
                {form.getFieldDecorator('is_active', {
                  initialValue: client ? !!client.is_active : true,
                  valuePropName: 'checked',
                })(
                  <Checkbox>
                    <strong>
                      <FormattedMessage id="Component.Administration.Clients.Form.IsActive" />
                    </strong>
                  </Checkbox>,
                )}
              </Col>
            </Row>
            {values.is_active && (
              <Row gutter={16}>
                <Col span={12}>
                  <Item
                    label={
                      <FormattedMessage id="Component.Administration.Clients.Form.ActiveTill" />
                    }
                  >
                    {form.getFieldDecorator('disabled_at', {
                      initialValue: client ? client.disabled_at : '',
                      rules: [],
                    })(<DatePicker disabledDate={disabledDate} type="date" />)}
                  </Item>
                </Col>
              </Row>
            )}
          </Card>
          <Card>
            <Row gutter={16}>
              <h2>
                <FormattedMessage id="Component.Administration.Clients.Analytics" />
              </h2>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label={<FormattedMessage id="Component.Administration.Clients.Analytics.Type" />}>
                  {props.form.getFieldDecorator('settings.analytic_type', {
                    initialValue: client && client.settings ? client.settings.analytic_type : 'matomo',
                    rules: [{
                      required: true,
                      message: intl.formatMessage({
                        id: 'Common.Field.Required',
                      }),
                    }],
                  })(
                    <Select style={{ width: '100%' }}>
                      {analyticsTypes.map(item => (
                        <Select.Option value={item.id} key={item.id}>
                          {/* <FormattedMessage id={`Common.Languages.Name.${item}`} />*/}
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>,
                  )}
                </Form.Item>
              </Col>
            </Row>
            {values.settings && values.settings.analytic_type === 'matomo' &&
            <Row gutter={16}>
              <Col span={12}>
                <Item
                  label={
                    <FormattedMessage id="Component.Administration.Clients.Analytics.BaseUri" />
                  }
                >
                  {form.getFieldDecorator('settings.matomo_base_uri', {
                    initialValue: client && client.settings ? client.settings.matomo_base_uri : '',
                    rules: [
                      {
                        required: true,
                        min: 3,
                        max: 255,
                        message: intl.formatMessage({
                          id: 'Common.Field.Required',
                        }),
                      },
                    ],
                  })(<Input maxLength={255} type="text" />)}
                </Item>
              </Col>
              <Col span={12}>
                <Item
                  label={
                    <FormattedMessage id="Component.Administration.Clients.Analytics.ApiKey" />
                  }
                >
                  {form.getFieldDecorator('settings.matomo_api_key', {
                    initialValue: client && client.settings ? client.settings.matomo_api_key : '',
                    rules: [
                      {
                        required: true,
                        min: 3,
                        max: 255,
                        message: intl.formatMessage({
                          id: 'Common.Field.Required',
                        }),
                      },
                    ],
                  })(<Input maxLength={255} type="text" />)}
                </Item>
              </Col>
            </Row>
            }
          </Card>
          <Card>
            <Row gutter={16}>
              <h2>
                <FormattedMessage id="Component.Administration.Clients.Settings" />
              </h2>
            </Row>
            <Row gutter={16}>
              <h3>
                <FormattedMessage id="Component.Administration.Clients.Features" />
              </h3>
            </Row>
            <Row gutter={16}>
              <Col>
                {form.getFieldDecorator('settings.feature_ai_outreach_agents', {
                  initialValue: client && client.settings ? !!client.settings.feature_ai_outreach_agents : false,
                  valuePropName: 'checked',
                })(
                  <Checkbox>
                    <FormattedMessage id="Component.Administration.Clients.Features.AiOutreachAgent" />
                  </Checkbox>,
                )}
                {form.getFieldDecorator('settings.feature_lxp_session_records', {
                  initialValue: client && client.settings ? !!client.settings.feature_lxp_session_records : false,
                  valuePropName: 'checked',
                })(
                  <Checkbox>
                    <FormattedMessage id="Component.Administration.Clients.Features.LxpSessionRecording" />
                  </Checkbox>,
                )}
                {form.getFieldDecorator('settings.feature_incoming_request_extra', {
                  initialValue: client && client.settings ? !!client.settings.feature_incoming_request_extra : false,
                  valuePropName: 'checked',
                })(
                  <Checkbox>
                    <FormattedMessage id="Component.Administration.Clients.Features.IncomingRequestExtra" />
                  </Checkbox>,
                )}
              </Col>
            </Row>
            <Row gutter={16}>
              {values.settings && values.settings.feature_ai_outreach_agents && (
                <React.Fragment>
                  <Col span={12}>
                    <Item label={<FormattedMessage id="Component.Administration.Clients.Features.ContactEnrichmentCallsBudget" />}>
                      {form.getFieldDecorator('contact_enrichment_calls_budget', {
                        initialValue: client ? client.contact_enrichment_calls_budget : 0,
                        rules: [
                          {
                            required: true,
                            message: intl.formatMessage({
                              id: 'Common.Field.Required',
                            }),
                          },
                        ],
                      })(
                        <InputNumber
                          maxLength={255}
                          min={0}
                          max={100000}
                          width={100}
                          style={{ fontFamily: 'normal' }}
                        />,
                      )}
                    </Item>
                  </Col>
                  <Col span={12}>
                    <Item label={<FormattedMessage id="Component.Administration.Clients.Features.ContactSearchesCallsBudget" />}>
                      {form.getFieldDecorator('contact_searches_calls_budget', {
                        initialValue: client ? client.contact_searches_calls_budget : 0,
                        rules: [
                          {
                            required: true,
                            message: intl.formatMessage({
                              id: 'Common.Field.Required',
                            }),
                          },
                        ],
                      })(
                        <InputNumber
                          maxLength={255}
                          min={0}
                          max={100000}
                          style={{ fontFamily: 'normal' }}
                        />,
                      )}
                    </Item>
                  </Col>
                </React.Fragment>
              )}
            </Row>
            <Row gutter={16} className={styles.marginTop16}>
              <Col span={4}>
                {form.getFieldDecorator('settings.feature_mark_as_isp', {
                  initialValue: client && client.settings ? !!client.settings.feature_mark_as_isp : false,
                  valuePropName: 'checked',
                })(
                  <Checkbox>
                    <strong>
                      <FormattedMessage id="Component.Administration.Clients.Features.MarkAsIsp" />
                    </strong>
                  </Checkbox>,
                )}
              </Col>
            </Row>
            <Row gutter={16}>
              {values.settings && values.settings.feature_mark_as_isp && (
                <React.Fragment>
                  <Col span={24}>
                    <IspEmails
                      form={form}
                      initialValue={client && client.settings && Array.isArray(client.settings.feature_mark_as_isp_emails) ? client.settings.feature_mark_as_isp_emails : []}
                    />
                  </Col>
                </React.Fragment>
              )}
            </Row>
            <Row gutter={16} className={styles.marginTop16}>
              <h3 className={styles.marginBottom8}>
                <FormattedMessage id="Component.Administration.Clients.Tokens" />
              </h3>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Item
                  label={
                    <FormattedMessage id="Component.Administration.Clients.Tokens.OpenAiApiToken" />
                  }
                >
                  {form.getFieldDecorator('settings.openai_api_token', {
                    initialValue: client && client.settings ? client.settings.openai_api_token : '',
                  })(<Input maxLength={255} type="text" />)}
                </Item>
              </Col>
            </Row>
          </Card>
          {!!clientId && <RecognitionUsages clientId={clientId} />}
          <ButtonBar cancelAction={goToClients} />
        </Form>
      </div>
    </Spin>
  );
};

export default connect(({ app, loading, clientPackages }) => ({ app, loading, clientPackages }))(
  Form.create()(injectIntl(ClientsItem)),
);



// WEBPACK FOOTER //
// ./src/components/Clients/ClientsItem/index.js